/**
 * Imperatively loads ref categories and attribute definitions into their stores
 * before calling claimResponseToFormValues at login time (before any React
 * component mounts). Mirrors the logic in useRefData / useAttributeData hooks
 * but without React hooks.
 *
 * Loading phases:
 *   1. Static base + truck + trailer ref categories
 *   2. Dynamic model category (depends on make label from phase 1)
 *   3. Body-info attribute definitions (depends on use/type label from phase 1)
 *   4. Body-info ref options for select fields (depends on attribute field IDs from phase 3)
 */
import { useRefStore } from '@/store/refStore'
import { log } from '@/utils/logger'
import { useAttributeStore } from '@/store/attributeStore'
import { fetchRefBatch } from '@/services/refService'
import { fetchAttributeDefinitions } from '@/services/attributeService'
import { setRefCacheRecord, getAttrCacheRecord, setAttrCacheRecord } from '@/services/idbService'
import { REF_KEYS } from '@/data/refKeys'
import { normaliseToKey } from '@/data/refCategories'
import type { ClaimGetResponse } from '@/types/claim'

const TRUCK_CATEGORIES = [
  REF_KEYS.TRUCK_MAKE,
  REF_KEYS.TRUCK_USE,
  REF_KEYS.TRUCK_GVWR,
  REF_KEYS.TRUCK_STYLE,
  REF_KEYS.TRUCK_TYPE,
  REF_KEYS.TRUCK_ENGINE_MFG,
  REF_KEYS.TRUCK_ENGINE_HP,
  REF_KEYS.TRUCK_TRANS_MFG,
  REF_KEYS.TRUCK_FRONT_AXLE_RATING,
  REF_KEYS.TRUCK_REAR_AXLE_RATING,
  REF_KEYS.TRUCK_SLEEPER_SIZE,
  REF_KEYS.TRUCK_COE_SIZE,
  REF_KEYS.TRUCK_APU,
  REF_KEYS.TRUCK_TRIM_LEVEL,
  REF_KEYS.TRUCK_WHEEL_TYPE,
  REF_KEYS.TRUCK_SUSPENSION,
  REF_KEYS.TRUCK_LIFT_GATE_CAPACITY,
  REF_KEYS.TRUCk_FIFTH_WHEEL_TYPE,
  REF_KEYS.TRUCK_HITCH_TYPE,
  REF_KEYS.TRUCK_VEHICLE_CONDITON,
]

const TRAILER_CATEGORIES = [
  REF_KEYS.TRAILER_MAKE,
  REF_KEYS.TRAILER_TYPE,
  REF_KEYS.TRAILER_EXTERIOR,
  REF_KEYS.TRAILER_LENGTH,
  REF_KEYS.TRAILER_HEIGHT,
  REF_KEYS.TRAILER_WIDTH,
  REF_KEYS.TRAILER_SUSPENSION,
  REF_KEYS.TRAILER_GAWR,
  REF_KEYS.TRAILER_INTERIOR_LINING,
  REF_KEYS.TRAILER_REAR_DOOR_TYPE,
  REF_KEYS.TRAILER_OS_WHEEL_TYPE,
  REF_KEYS.TRAILER_IS_WHEEL_TYPE,
  REF_KEYS.TRAILER_VEHICLE_CONDITON,
]

const BASE_CATEGORIES = [
  REF_KEYS.TYPE_OF_LOSS,
  REF_KEYS.VEHICLE_TYPE,
  REF_KEYS.COUNTRY_STATE,
  REF_KEYS.SECTION,
]

const BATCH_CHUNK_SIZE = 20

async function batchLoad(categories: string[]): Promise<void> {
  const currentCache = useRefStore.getState().cache
  const notInMemory = categories.filter((c) => !currentCache[c])
  if (notInMemory.length === 0) return

  // Split into chunks to respect the backend's max-20-categories-per-batch limit.
  // Chunks are fetched in parallel to keep total latency low.
  const chunks: string[][] = []
  for (let i = 0; i < notInMemory.length; i += BATCH_CHUNK_SIZE) {
    chunks.push(notInMemory.slice(i, i + BATCH_CHUNK_SIZE))
  }

  const { setCache } = useRefStore.getState()
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const results = await fetchRefBatch(chunk)
        for (const [category, options] of Object.entries(results)) {
          if (options?.length) {
            setCache(category, options)
            setRefCacheRecord({ category, options, cachedAt: Date.now() }).catch((e) => log.error('IDB setRefCacheRecord failed', e instanceof Error ? e : new Error(String(e))))
          }
        }
      } catch (err) {
        log.error('[preloadRefCategories] Batch fetch failed', err instanceof Error ? err : new Error(String(err)), { chunk: chunk.join(',') })
      }
    })
  )
}

export async function preloadRefCategories(claimData?: ClaimGetResponse): Promise<void> {
  // Phase 1: load all static base + vehicle categories
  const allCategories = [...new Set([...BASE_CATEGORIES, ...TRUCK_CATEGORIES, ...TRAILER_CATEGORIES])]
  await batchLoad(allCategories)

  if (!claimData?.vehicle) return

  const { getLabelById } = useRefStore.getState()
  const vehicle = claimData.vehicle
  const vehicleTypeLabel = getLabelById(REF_KEYS.VEHICLE_TYPE, claimData.vehicleTypeId ?? 0)
  const vehicleKind = vehicleTypeLabel === 'Truck/Tractor' ? 'truck'
    : vehicleTypeLabel === 'Trailer' ? 'trailer' : ''

  // Phase 2: model category — depends on make label resolved from Phase 1
  const modelCategories: string[] = []
  if (vehicleKind === 'truck' && vehicle.makeId != null) {
    const makeLabel = getLabelById(REF_KEYS.TRUCK_MAKE, vehicle.makeId)
    if (makeLabel) modelCategories.push(`TRUCK_MODEL_${normaliseToKey(makeLabel)}`)
  } else if (vehicleKind === 'trailer' && vehicle.makeId != null) {
    const makeLabel = getLabelById(REF_KEYS.TRAILER_MAKE, vehicle.makeId)
    if (makeLabel) modelCategories.push(`TRAILER_MODEL_${normaliseToKey(makeLabel)}`)
  }
  if (vehicleKind === 'truck' && vehicle.truck?.transMfg != null) {
    const transMfgLabel = getLabelById(REF_KEYS.TRUCK_TRANS_MFG, vehicle.truck.transMfg)
    if (transMfgLabel) modelCategories.push(`TRUCK_TRANS_SPEED_${normaliseToKey(transMfgLabel)}`)
  }
  if (modelCategories.length > 0) {
    await batchLoad(modelCategories)
  }

  // Phase 2b: Use/Type Subtype label — depends on use/type label from Phase 1, needed both
  // to display the Subtype value itself and to resolve the subtype-scoped attribute category below.
  let useLabel: string | undefined
  let typeLabel: string | undefined
  let useSubtypeLabel: string | undefined
  let typeSubtypeLabel: string | undefined

  if (vehicleKind === 'truck' && vehicle.truck?.useTypeId != null) {
    useLabel = getLabelById(REF_KEYS.TRUCK_USE, vehicle.truck.useTypeId)
  } else if (vehicleKind === 'trailer' && vehicle.trailer?.trailerTypeId != null) {
    typeLabel = getLabelById(REF_KEYS.TRAILER_TYPE, vehicle.trailer.trailerTypeId)
  }

  if (vehicleKind === 'truck' && useLabel && vehicle.truck?.useSubtypeId != null) {
    const subtypeCategory = `TRUCK_USE_SUBTYPE_${normaliseToKey(useLabel)}`
    await batchLoad([subtypeCategory])
    useSubtypeLabel = getLabelById(subtypeCategory, vehicle.truck.useSubtypeId)
  } else if (vehicleKind === 'trailer' && typeLabel && vehicle.trailer?.typeSubtypeId != null) {
    const subtypeCategory = `TRAILER_TYPE_SUBTYPE_${normaliseToKey(typeLabel)}`
    await batchLoad([subtypeCategory])
    typeSubtypeLabel = getLabelById(subtypeCategory, vehicle.trailer.typeSubtypeId)
  }

  // Phase 3: body-info attribute definitions — category includes the Subtype suffix when
  // applicable, matching the TRUCK_BODY_<USE>_<SUBTYPE> convention used at save time.
  let attrCategory: string | null = null

  if (vehicleKind === 'truck' && useLabel) {
    attrCategory = useSubtypeLabel
      ? `TRUCK_BODY_${normaliseToKey(useLabel)}_${normaliseToKey(useSubtypeLabel)}`
      : `TRUCK_BODY_${normaliseToKey(useLabel)}`
  } else if (vehicleKind === 'trailer' && typeLabel) {
    attrCategory = typeSubtypeLabel
      ? `TRAILER_BODY_${normaliseToKey(typeLabel)}_${normaliseToKey(typeSubtypeLabel)}`
      : `TRAILER_BODY_${normaliseToKey(typeLabel)}`
  }

  if (!attrCategory) return

  const { setCache: setAttrCache, cache: attrCache } = useAttributeStore.getState()

  let selectFields: { attribute_name: string; ref_category?: string | null }[] = []

  if (attrCache[attrCategory]?.length) {
    selectFields = attrCache[attrCategory].filter((a) => a.control_type === 'select')
  } else {
    try {
      const record = await getAttrCacheRecord(attrCategory)
      if (record?.attributes?.length) {
        setAttrCache(attrCategory, record.attributes)
        selectFields = record.attributes.filter((a) => a.control_type === 'select')
      } else {
        const attributes = await fetchAttributeDefinitions(attrCategory)
        if (attributes.length) {
          setAttrCache(attrCategory, attributes)
          setAttrCacheRecord({ category: attrCategory, attributes, cachedAt: Date.now() }).catch((e) => log.error('IDB setAttrCacheRecord failed', e instanceof Error ? e : new Error(String(e))))
          selectFields = attributes.filter((a) => a.control_type === 'select')
        }
      }
    } catch (err) {
      log.error('[preloadRefCategories] Attribute definition fetch failed', err instanceof Error ? err : new Error(String(err)))
    }
  }

  // Phase 4: body-info ref options for select fields — depends on attribute field IDs from Phase 3.
  // Prefer each attribute's own `ref_category` (shared/common dropdown values) when present,
  // falling back to the vehicle-specific derived key — mirrors resolveBodyFieldCategory in claimPayloadMapper.ts.
  if (selectFields.length > 0) {
    const bodyRefCategories = selectFields
      .map(({ attribute_name: fieldId, ref_category: refCategory }) => {
        if (refCategory) return refCategory
        if (attrCategory) return `${attrCategory}_${normaliseToKey(fieldId)}`
        return null
      })
      .filter(Boolean) as string[]

    if (bodyRefCategories.length > 0) {
      await batchLoad(bodyRefCategories)
    }
  }
}
